// Attempt analysis: from a timeline of pose frames to "where did the feet land,
// what was corrected afterwards, and is the evidence good enough to say so".
//
// The analysis deliberately keeps two observations apart:
//   • initial placement – where the feet first come to rest after the pop-up;
//   • settled placement – where they are during the controlled hold.
// A landing that is only corrected into place afterwards must never be scored
// as clean, and a landing that happened out of view is reported as such rather
// than silently replaced by the later stance.

import { angleDiff, dist, median, medianPoint, meanAngle, robustSigma, type Vec2 } from './geometry';
import { observeAll, type FootObs, type FrameObs } from './features';
import { toeSideSign, type PlaneMapping, type Stance } from './mapping';
import { ALGORITHM_VERSION, type AnalysisParams } from './params';
import type { PoseFrame } from './pose';
import type { FootTarget, Reference } from './reference';

export type Outcome = 'clean' | 'adjust' | 'unassessed' | 'none';
export type FootRole = 'front' | 'rear';

export interface FootPlacement {
  centre: Vec2;
  angle: number;
  /** Error vs target, cm. along: + toward the nose. across: + toward the toes. */
  along: number;
  across: number;
  /** Distance to target in tolerance units (1 = edge of the target region). */
  score: number;
  /** Signed foot-angle error, degrees; + means toes turned toward the nose. */
  angleError: number;
  onTarget: boolean;
}

export interface Placement {
  t0: number;
  t1: number;
  front: FootPlacement;
  rear: FootPlacement;
  /** Front-to-rear distance along the stringer minus the target's, cm. */
  widthError: number;
  onTarget: boolean;
}

export interface Adjustment {
  foot: FootRole;
  kind: 'step' | 'pivot';
  /** When the foot came to rest again, ms. */
  t: number;
  from: Vec2;
  to: Vec2;
  /** Displacement, cm (along: + toward nose, across: + toward toes). */
  along: number;
  across: number;
  rotationDeg: number;
  /** True when the move brought the foot closer to its target. */
  corrective: boolean;
}

export type Issue =
  | 'noPose'
  | 'noRise'
  | 'noLanding'
  | 'frontHiddenAtLanding'
  | 'rearHiddenAtLanding'
  | 'noHold'
  | 'holdHidden'
  | 'lowCoverage';

export interface AttemptEvents {
  cue?: number;
  /** First detectable movement of the pop-up (start of the shoulder rise). */
  onset?: number;
  /** Front foot reaches the front half of the stance. */
  arrival?: number;
  /** Both feet planted (initial placement established). */
  landing?: number;
  holdStart?: number;
  holdEnd?: number;
}

export interface AnalysisSeries {
  t: number[];
  /** Body rise, 0 = prone baseline, 1 = standing in stance. */
  rise: (number | null)[];
  frontY: (number | null)[];
  rearY: (number | null)[];
  frontStill: boolean[];
  rearStill: boolean[];
}

export interface AttemptAnalysis {
  version: number;
  outcome: Outcome;
  initial: Placement | null;
  settled: Placement | null;
  adjustments: Adjustment[];
  events: AttemptEvents;
  timing: { popupMs: number | null; reactionMs: number | null };
  issues: Issue[];
  holdComplete: boolean;
  /** Fraction of frames (from the start of the movement) in which a person was detected. */
  coverage: number;
  series: AnalysisSeries;
}

export interface AnalysisContext {
  mapping: PlaneMapping;
  reference: Reference;
  params: AnalysisParams;
  stance: Stance;
  /** Time of the "go" cue on the timeline, if there was one. */
  cueTime?: number;
}

interface Assigned {
  front: FootObs | null;
  rear: FootObs | null;
}

interface Segment {
  i0: number;
  i1: number;
  t0: number;
  t1: number;
  pos: Vec2;
  angle: number;
}

// ---------------------------------------------------------------------------

export function analyzeAttempt(frames: readonly PoseFrame[], ctx: AnalysisContext): AttemptAnalysis {
  const { reference: ref, params: P } = ctx;
  const toe = toeSideSign(ctx.stance);
  const footLength = (ref.front.length + ref.rear.length) / 2;
  const obs = observeAll(frames, ctx.mapping, { minVisibility: P.minVisibility, footLength });
  const times = obs.map((o) => o.t);
  const W = Math.max(15, ref.front.centre.y - ref.rear.centre.y); // target separation along the stringer
  const issues: Issue[] = [];
  const events: AttemptEvents = { cue: ctx.cueTime };

  const assigned = assignFeet(obs, ref, W, footLength);
  const front = assigned.map((a) => a.front);
  const rear = assigned.map((a) => a.rear);

  // --- Body rise and movement onset -------------------------------------
  const riseInfo = detectRise(obs, ref.standHeight, ctx.cueTime);
  const rise = riseInfo.rise;
  if (riseInfo.onset !== undefined) events.onset = riseInfo.onset;

  // --- Front foot arrival ---------------------------------------------------
  const t0 = times[0] ?? 0;
  const searchFrom = riseInfo.onset ?? (riseInfo.riseTime !== undefined ? riseInfo.riseTime - 800 : ctx.cueTime ?? t0);
  const proneY = medianOf(
    obs.filter((o) => o.t >= searchFrom - 400 && o.t <= searchFrom).flatMap((o) => [o.L, o.R]),
    (f) => f.centre.y,
  );
  const arrivalY = proneY !== null ? Math.max(proneY + 0.35 * W, ref.rear.centre.y + 0.2 * W) : ref.rear.centre.y + 0.45 * W;
  let arrivalIdx = -1;
  for (let i = 0; i < obs.length; i++) {
    if (times[i] < searchFrom) continue;
    const f = front[i];
    if (!f || f.centre.y < arrivalY) continue;
    if (rise[i] !== null && rise[i]! < 0.2) continue;
    arrivalIdx = i;
    break;
  }
  if (arrivalIdx >= 0) events.arrival = times[arrivalIdx];

  // --- Stillness segments ---------------------------------------------------
  // The stillness window must span a few frames even when inference is slow.
  const dt = frameInterval(times);
  const touchMs = Math.max(P.touchMs, 2.2 * dt);
  const gapMs = Math.max(150, 2.5 * dt);
  const frontStill = stillFlags(front, times, touchMs, P.stillRadius);
  const rearStill = stillFlags(rear, times, touchMs, P.stillRadius);
  const frontSegs = segments(front, frontStill, times, touchMs, P.stillRadius, gapMs);
  const rearSegs = segments(rear, rearStill, times, touchMs, P.stillRadius, gapMs);

  // --- Initial placement ---------------------------------------------------
  // The landing is the first time each foot comes to rest once the body is up
  // and the foot has left where it lay — wherever that is. (Waiting for the
  // front foot to reach the front half of the stance would miss a short
  // landing that is then stepped into place, and score the correction.)
  let initial: Placement | null = null;
  let frontInit: Segment | null = null;
  let rearInit: Segment | null = null;
  const upAt = riseInfo.riseTime ?? events.arrival;
  if (upAt !== undefined) {
    const upFrom = Math.max(searchFrom, upAt - 150);
    const leftProneSpot = (pos: Vec2) =>
      proneY === null ? pos.y >= ref.rear.centre.y - 0.15 * W : pos.y >= proneY + P.stepMin;
    frontInit = frontSegs.find((sg) => sg.t0 >= upFrom && leftProneSpot(sg.pos)) ?? null;
    if (frontInit) {
      const tFC = frontInit.t0;
      // The landing counts as observed only if we saw the foot travelling in
      // and kept seeing it right up to touchdown; otherwise it may have landed
      // elsewhere and been corrected out of view.
      const seenArriving = sawApproach(front, times, tFC, frontInit.pos, 1.5 * P.stillRadius, gapMs, touchMs);
      if (!seenArriving) issues.push('frontHiddenAtLanding');
      // Back foot: its first rest after leaving the prone spot — which may come
      // well before the front foot lands — or the rest it holds through the
      // front foot's touchdown if it never moved.
      const covering = rearSegs.find((sg) => sg.t0 <= tFC + 50 && sg.t1 >= tFC - 50) ?? null;
      const earliest = rearSegs.find((sg) => sg.t0 >= searchFrom + 100 && sg.t0 <= tFC + 300 && (proneY === null || sg.pos.y >= proneY + P.stepMin)) ?? null;
      rearInit = [covering, earliest].filter((x): x is Segment => x !== null).sort((a, b) => a.t0 - b.t0)[0] ?? null;
      const rearSeen = rearInit !== null && countValid(rear, times, tFC - 150, tFC + 150) >= 2;
      if (!rearInit || !rearSeen) issues.push('rearHiddenAtLanding');
      const tLand = Math.max(tFC, rearInit ? rearInit.t0 : tFC);
      events.landing = tLand;
      if (seenArriving && rearInit && rearSeen) {
        initial = placement(frontInit.pos, frontInit.angle, rearInit.pos, rearInit.angle, tFC, tLand + touchMs, ref, P, toe);
      }
    } else {
      issues.push('noLanding');
    }
  }

  // --- Hold / settled placement -------------------------------------------
  const holdAfter = events.landing ?? events.arrival ?? (riseInfo.riseTime !== undefined ? riseInfo.riseTime + 200 : undefined);
  let settled: Placement | null = null;
  let holdFront: Segment | null = null;
  let holdRear: Segment | null = null;
  if (holdAfter !== undefined) {
    let best: { a: Segment; b: Segment; t0: number; t1: number } | null = null;
    for (const a of frontSegs) {
      for (const b of rearSegs) {
        const s = Math.max(a.t0, b.t0, holdAfter - 50);
        const e = Math.min(a.t1, b.t1);
        if (e - s >= P.holdMs && (!best || e > best.t1)) best = { a, b, t0: s, t1: e };
      }
    }
    if (best) {
      holdFront = best.a;
      holdRear = best.b;
      events.holdStart = best.t0;
      events.holdEnd = best.t1;
      const fIn = within(front, times, best.t0, best.t1);
      const rIn = within(rear, times, best.t0, best.t1);
      settled = placement(
        medianPoint(fIn.map((f) => f.centre)), meanAngle(fIn.map((f) => f.angle)),
        medianPoint(rIn.map((f) => f.centre)), meanAngle(rIn.map((f) => f.angle)),
        best.t0, best.t1, ref, P, toe,
      );
    } else {
      const after = obs.filter((o) => o.t >= holdAfter);
      const visible = after.filter((o) => o.L && o.R).length;
      issues.push(after.length > 0 && visible / after.length < 0.5 ? 'holdHidden' : 'noHold');
    }
  }
  const holdComplete = settled !== null;

  // --- Adjustments between landing and hold -------------------------------
  const adjustments: Adjustment[] = [];
  if (frontInit && holdFront && settled) {
    adjustments.push(...footAdjustments('front', frontInit, frontSegs, settled.front, holdFront, ref.front, P, toe));
  }
  if (rearInit && holdRear && settled) {
    adjustments.push(...footAdjustments('rear', rearInit, rearSegs, settled.rear, holdRear, ref.rear, P, toe));
  }
  adjustments.sort((a, b) => a.t - b.t);

  // --- Coverage & issues -----------------------------------------------------
  const startIdx = Math.max(0, times.findIndex((t) => t >= (events.onset ?? searchFrom)));
  const span = obs.slice(startIdx);
  const coverage = span.length ? span.filter((o) => o.present).length / span.length : 0;
  if (!obs.some((o) => o.present)) issues.push('noPose');
  else if (riseInfo.riseTime === undefined && ref.standHeight > 0) issues.push('noRise');
  if (coverage < 0.6 && obs.some((o) => o.present)) issues.push('lowCoverage');

  // --- Outcome ---------------------------------------------------------------
  const steps = adjustments.filter((a) => a.kind === 'step');
  let outcome: Outcome;
  // Feet already resting in place before the movement window means the rider
  // was standing all along, not popping up.
  const stoodStill =
    !frontInit && events.arrival !== undefined && frontSegs.some((s) => s.t0 < searchFrom && s.t1 >= events.arrival!);
  const sawPopup = !stoodStill && (riseInfo.riseTime !== undefined || events.arrival !== undefined);
  if (!sawPopup) {
    outcome = obs.some((o) => o.present) && !issues.includes('lowCoverage') ? 'none' : 'unassessed';
  } else if (initial) {
    if (!initial.onTarget || steps.length > 0) outcome = 'adjust';
    else if (!settled) outcome = issues.includes('noHold') ? 'adjust' : 'unassessed';
    else outcome = settled.onTarget ? 'clean' : 'adjust';
  } else if (settled && !settled.onTarget) {
    outcome = 'adjust';
  } else {
    outcome = 'unassessed';
  }

  // --- Timing -----------------------------------------------------------------
  const onsetOk = riseInfo.onset !== undefined && riseInfo.onsetConfident;
  const popupMs = onsetOk && initial && events.landing !== undefined ? events.landing - riseInfo.onset! : null;
  const reactionMs =
    onsetOk && ctx.cueTime !== undefined && riseInfo.onset! >= ctx.cueTime + 80 ? riseInfo.onset! - ctx.cueTime : null;

  return {
    version: ALGORITHM_VERSION,
    outcome,
    initial,
    settled,
    adjustments,
    events,
    timing: { popupMs, reactionMs },
    issues: [...new Set(issues)],
    holdComplete,
    coverage,
    series: {
      t: times,
      rise,
      frontY: front.map((f) => (f ? f.centre.y : null)),
      rearY: rear.map((f) => (f ? f.centre.y : null)),
      frontStill,
      rearStill,
    },
  };
}

// ---------------------------------------------------------------------------
// Foot identity. MediaPipe's left/right labels can swap mid-movement, so the
// front foot is defined as the one nearer the nose. Frames where the feet are
// clearly separated along the stringer (e.g. the hold) are anchors; identity
// is carried from the nearest anchor, frame by frame in both directions, by
// matching each observation to the closest carried position. A lifted foot's
// floor projection is unreliable, so implausible jumps don't move the carried
// position. Frames out of reach of any anchor fall back on the targets.

function assignFeet(obs: readonly FrameObs[], ref: Reference, W: number, footLength: number): Assigned[] {
  const n = obs.length;
  // A lifted foot projects onto the floor stretched and displaced, so anchors
  // also require both feet to look planted: true-to-life length, on the board.
  const planted = (f: FootObs) => f.length >= 0.7 * footLength && f.length <= 1.4 * footLength && Math.abs(f.centre.x) <= 0.6 * W + 20;
  const isAnchor = obs.map((o) => !!(o.L && o.R && Math.abs(o.L.centre.y - o.R.centre.y) >= 0.35 * W && planted(o.L) && planted(o.R)));
  const byOrder = (o: FrameObs): Assigned => (o.L!.centre.y >= o.R!.centre.y ? { front: o.L, rear: o.R } : { front: o.R, rear: o.L });
  const GATE = 45; // cm a carried position may move in one step

  // When position can't decide (a lifted foot's projection crossing the other
  // foot), the model's own left/right label — usually right — breaks the tie.
  const AMBIGUOUS = 10;
  type Carried = Assigned & { since: number };
  const pass = (forward: boolean): (Carried | null)[] => {
    const res: (Carried | null)[] = new Array(n).fill(null);
    let state: { f: Vec2; r: Vec2; anchorT: number; frontLabel: 'L' | 'R' } | null = null;
    for (let k = 0; k < n; k++) {
      const i = forward ? k : n - 1 - k;
      const o = obs[i];
      if (isAnchor[i]) {
        const a = byOrder(o);
        state = { f: a.front!.centre, r: a.rear!.centre, anchorT: o.t, frontLabel: a.front!.label };
        res[i] = { ...a, since: 0 };
        continue;
      }
      if (!state) continue;
      const feet = [o.L, o.R].filter((f): f is FootObs => f !== null);
      let a: Assigned = { front: null, rear: null };
      if (feet.length === 2) {
        const [x, y] = feet;
        const keepCost = dist(x.centre, state.f) + dist(y.centre, state.r);
        const swapCost = dist(y.centre, state.f) + dist(x.centre, state.r);
        const keep = Math.abs(keepCost - swapCost) < AMBIGUOUS ? x.label === state.frontLabel : keepCost <= swapCost;
        a = keep ? { front: x, rear: y } : { front: y, rear: x };
      } else if (feet.length === 1) {
        const f = feet[0];
        const dF = dist(f.centre, state.f);
        const dR = dist(f.centre, state.r);
        const isFront = Math.abs(dF - dR) < AMBIGUOUS ? f.label === state.frontLabel : dF <= dR;
        a = isFront ? { front: f, rear: null } : { front: null, rear: f };
      }
      if (a.front && dist(a.front.centre, state.f) <= GATE) state.f = a.front.centre;
      if (a.rear && dist(a.rear.centre, state.r) <= GATE) state.r = a.rear.centre;
      res[i] = { ...a, since: Math.abs(o.t - state.anchorT) };
    }
    return res;
  };
  const fwd = pass(true);
  const bwd = pass(false);

  return obs.map((o, i) => {
    if (isAnchor[i]) return byOrder(o);
    const f = fwd[i];
    const b = bwd[i];
    const pick = f && b ? (f.since <= b.since ? f : b) : f ?? b;
    if (pick) return { front: pick.front, rear: pick.rear };
    // No anchor anywhere: nearest target.
    const feet = [o.L, o.R].filter((x): x is FootObs => x !== null);
    if (feet.length === 2) {
      const [x, y] = feet;
      const keep = dist(x.centre, ref.front.centre) + dist(y.centre, ref.rear.centre) <= dist(y.centre, ref.front.centre) + dist(x.centre, ref.rear.centre);
      return keep ? { front: x, rear: y } : { front: y, rear: x };
    }
    if (feet.length === 1) {
      const x = feet[0];
      return dist(x.centre, ref.front.centre) <= dist(x.centre, ref.rear.centre) ? { front: x, rear: null } : { front: null, rear: x };
    }
    return { front: null, rear: null };
  });
}

// ---------------------------------------------------------------------------
// Rise: feet-to-shoulders height relative to the standing reference, rescaled
// so the prone baseline is 0 and the reference stance is 1.

function detectRise(obs: readonly FrameObs[], standHeight: number, cueTime?: number) {
  const z = obs.map((o) => (o.height !== null && standHeight > 0 ? o.height / standHeight : null));
  const t0 = obs[0]?.t ?? 0;
  const baseEnd = cueTime !== undefined ? cueTime + 150 : t0 + 600;
  const baseStart = cueTime !== undefined ? cueTime - 1500 : t0;
  const base = z.filter((v, i): v is number => v !== null && obs[i].t >= baseStart && obs[i].t <= baseEnd);
  let zBase = base.length >= 3 ? median(base) : null;
  // A "baseline" that is already near standing means the rider wasn't prone.
  if (zBase !== null && zBase > 0.65) zBase = null;
  const range = zBase !== null ? Math.max(0.3, 1 - zBase) : 1;
  const sigma = base.length >= 3 ? Math.max(0.02, robustSigma(base)) : 0.05;
  const rise = z.map((v) => (v === null ? null : (v - (zBase ?? 0.2)) / range));

  const searchFrom = cueTime ?? (zBase !== null ? baseEnd : t0);
  let riseIdx = -1;
  for (let i = 0; i < obs.length; i++) {
    if (obs[i].t < searchFrom || rise[i] === null || rise[i]! < 0.45) continue;
    let sustained = true;
    for (let j = i + 1; j < obs.length && obs[j].t <= obs[i].t + 100; j++) {
      if (rise[j] !== null && rise[j]! < 0.3) sustained = false;
    }
    if (sustained) {
      riseIdx = i;
      break;
    }
  }
  if (riseIdx < 0) return { rise, riseTime: undefined, onset: undefined, onsetConfident: false };

  let onset: number | undefined;
  let onsetConfident = false;
  if (zBase !== null) {
    const thrLow = Math.max(0.06, (3 * sigma) / range);
    let j = riseIdx;
    let prevT = obs[riseIdx].t;
    let gap = 0;
    while (j > 0) {
      const k = j - 1;
      if (rise[k] !== null) {
        gap = Math.max(gap, prevT - obs[k].t);
        prevT = obs[k].t;
        if (rise[k]! <= thrLow) {
          // Interpolate the crossing between k and the next valid sample above it.
          let n = k + 1;
          while (n < riseIdx && rise[n] === null) n++;
          const a = rise[k]!;
          const b = rise[n] ?? a;
          const f = b > a ? (thrLow - a) / (b - a) : 0;
          onset = obs[k].t + Math.min(1, Math.max(0, f)) * (obs[n].t - obs[k].t);
          break;
        }
      }
      j = k;
    }
    onsetConfident = onset !== undefined && gap <= 150 && onset >= baseStart;
  }
  return { rise, riseTime: obs[riseIdx].t, onset, onsetConfident };
}

// ---------------------------------------------------------------------------
// Stillness: a foot is still at frame i when its samples over the next
// `windowMs` stay within `radius` of their median.

function stillFlags(track: readonly (FootObs | null)[], times: readonly number[], windowMs: number, radius: number): boolean[] {
  return track.map((f, i) => {
    if (!f) return false;
    const pts: Vec2[] = [];
    let last = times[i];
    for (let j = i; j < track.length && times[j] <= times[i] + windowMs; j++) {
      const g = track[j];
      if (g) {
        pts.push(g.centre);
        last = times[j];
      }
    }
    // Two frames suffice at low frame rates, so brief touchdowns still register.
    if (pts.length < 2 || last - times[i] < 0.45 * windowMs) return false;
    const m = medianPoint(pts);
    // Longer windows may contain one noisy sample.
    return pts.filter((p) => dist(p, m) <= radius).length >= Math.ceil(0.75 * pts.length);
  });
}

/** Groups still frames into rest periods, splitting slow drifts. */
function segments(
  track: readonly (FootObs | null)[],
  still: readonly boolean[],
  times: readonly number[],
  windowMs: number,
  radius: number,
  gapMs: number,
): Segment[] {
  const segs: Segment[] = [];
  let cur: number[] = [];
  let anchor: Vec2 | null = null;
  const flush = () => {
    if (cur.length === 0) return;
    const i0 = cur[0];
    const iLast = cur[cur.length - 1];
    // Extend through the last still frame's look-ahead window.
    let i1 = iLast;
    for (let j = iLast + 1; j < track.length && times[j] <= times[iLast] + windowMs; j++) if (track[j]) i1 = j;
    const members: FootObs[] = [];
    for (let j = i0; j <= i1; j++) if (track[j]) members.push(track[j]!);
    segs.push({
      i0,
      i1,
      t0: times[i0],
      t1: times[i1],
      pos: medianPoint(members.map((m) => m.centre)),
      angle: meanAngle(members.map((m) => m.angle)),
    });
    cur = [];
    anchor = null;
  };
  // A rest period survives brief glitches (a dropped or jittery frame): it only
  // ends after `gapMs` without a still sample, or when the foot reappears
  // somewhere else.
  for (let i = 0; i < track.length; i++) {
    const f = track[i];
    if (!f || !still[i]) continue;
    const lastStill = cur.length ? cur[cur.length - 1] : -1;
    const gapTooLong = lastStill >= 0 && times[i] - times[lastStill] > gapMs;
    if (cur.length && (gapTooLong || (anchor && dist(f.centre, anchor) > radius * 1.5))) flush();
    if (!cur.length) anchor = f.centre;
    cur.push(i);
  }
  flush();
  return mergeRests(segs, track, radius);
}

/**
 * Joins consecutive rest periods at the same spot when nothing between them
 * looks like movement (every sample stays near the spot). Noise spikes then
 * can't fragment a hold, while a real step — which changes position by more
 * than `radius` — is never merged away.
 */
function mergeRests(segs: Segment[], track: readonly (FootObs | null)[], radius: number): Segment[] {
  const out: Segment[] = [];
  for (const seg of segs) {
    const prev = out[out.length - 1];
    if (prev && seg.t0 - prev.t1 <= 400 && dist(prev.pos, seg.pos) <= radius) {
      const spot = { x: (prev.pos.x + seg.pos.x) / 2, y: (prev.pos.y + seg.pos.y) / 2 };
      let quiet = true;
      for (let j = prev.i1 + 1; j < seg.i0; j++) {
        const f = track[j];
        if (f && dist(f.centre, spot) > 2.5 * radius) quiet = false;
      }
      if (quiet) {
        const members: FootObs[] = [];
        for (let j = prev.i0; j <= seg.i1; j++) if (track[j]) members.push(track[j]!);
        out[out.length - 1] = {
          i0: prev.i0,
          i1: seg.i1,
          t0: prev.t0,
          t1: seg.t1,
          pos: medianPoint(members.map((m) => m.centre)),
          angle: meanAngle(members.map((m) => m.angle)),
        };
        continue;
      }
    }
    out.push(seg);
  }
  return out;
}

// ---------------------------------------------------------------------------

function footAdjustments(
  role: FootRole,
  initial: Segment,
  segs: readonly Segment[],
  settled: FootPlacement,
  holdSeg: Segment,
  target: FootTarget,
  P: AnalysisParams,
  toe: 1 | -1,
): Adjustment[] {
  // Rest positions from the landing to the hold. Brief pauses mid-movement are
  // not replants, so intermediate rests must last a while to count.
  const between = segs.filter((s) => s.t0 > initial.t0 && s.t0 < holdSeg.t0 && s.t1 - s.t0 >= REPLANT_MS);
  const plants: { pos: Vec2; angle: number; t: number }[] = [{ pos: initial.pos, angle: initial.angle, t: initial.t0 }];
  for (const s of between) plants.push({ pos: s.pos, angle: s.angle, t: s.t0 });
  plants.push({ pos: settled.centre, angle: settled.angle, t: holdSeg.t0 });
  const out: Adjustment[] = [];
  let prev = plants[0];
  for (let k = 1; k < plants.length; k++) {
    const p = plants[k];
    const d = dist(p.pos, prev.pos);
    const rot = (angleDiff(p.angle, prev.angle) * 180) / Math.PI;
    let kind: 'step' | 'pivot' | null = null;
    if (d >= P.stepMin) kind = 'step';
    else if (Math.abs(rot) >= P.pivotDeg) kind = 'pivot';
    if (!kind) continue; // small settle: ignored, and the reference rest position stays put
    const err = (q: Vec2) => Math.hypot((q.y - target.centre.y) / P.tolAlong, (q.x - target.centre.x) / P.tolAcross);
    out.push({
      foot: role,
      kind,
      t: p.t,
      from: prev.pos,
      to: p.pos,
      along: p.pos.y - prev.pos.y,
      across: (p.pos.x - prev.pos.x) * toe,
      rotationDeg: rot * toe,
      corrective: err(p.pos) < err(prev.pos),
    });
    prev = p;
  }
  return out;
}

/** Shortest rest between landing and hold that counts as a separate plant, ms. */
const REPLANT_MS = 200;

function evaluateFoot(pos: Vec2, angle: number, target: FootTarget, P: AnalysisParams, toe: 1 | -1): FootPlacement {
  const along = pos.y - target.centre.y;
  const across = (pos.x - target.centre.x) * toe;
  const score = Math.hypot(along / P.tolAlong, across / P.tolAcross);
  const angleError = ((angleDiff(angle, target.angle) * 180) / Math.PI) * toe;
  const onTarget = score <= 1 && (!P.checkAngle || Math.abs(angleError) <= P.angleTolDeg);
  return { centre: pos, angle, along, across, score, angleError, onTarget };
}

function placement(
  fPos: Vec2, fAngle: number, rPos: Vec2, rAngle: number,
  t0: number, t1: number, ref: Reference, P: AnalysisParams, toe: 1 | -1,
): Placement {
  const front = evaluateFoot(fPos, fAngle, ref.front, P, toe);
  const rear = evaluateFoot(rPos, rAngle, ref.rear, P, toe);
  return {
    t0,
    t1,
    front,
    rear,
    widthError: fPos.y - rPos.y - (ref.front.centre.y - ref.rear.centre.y),
    onTarget: front.onTarget && rear.onTarget,
  };
}

// ---------------------------------------------------------------------------

function medianOf<T>(items: readonly (T | null)[], f: (t: T) => number): number | null {
  const v = items.filter((x): x is T => x !== null).map(f);
  return v.length ? median(v) : null;
}

/**
 * True when the foot was seen moving and then, within `maxGapMs`, seen at its
 * resting spot — i.e. we watched it arrive. A foot that only shows up once
 * it is already resting may have landed elsewhere and been moved out of view.
 * Brief gaps after arrival are fine as long as every sighting stays on the spot.
 */
function sawApproach(
  track: readonly (FootObs | null)[], times: readonly number[], tTouch: number, rest: Vec2,
  nearRadius: number, maxGapMs: number, touchMs: number,
): boolean {
  let lastMoving = -Infinity;
  let arrivedAt: number | null = null;
  let arrivalGap = Infinity;
  let prevNear = -Infinity;
  let gapAtRest = 0;
  for (let i = 0; i < track.length; i++) {
    const t = times[i];
    const f = track[i];
    if (!f || t < tTouch - 600) continue;
    if (t > tTouch + touchMs) break;
    if (dist(f.centre, rest) > nearRadius) {
      lastMoving = t;
      arrivedAt = null;
      gapAtRest = 0;
    } else if (arrivedAt === null) {
      arrivedAt = t;
      arrivalGap = t - lastMoving;
      prevNear = t;
    } else {
      gapAtRest = Math.max(gapAtRest, t - prevNear);
      prevNear = t;
    }
  }
  return arrivedAt !== null && arrivalGap <= maxGapMs && gapAtRest <= 400;
}

function frameInterval(times: readonly number[]): number {
  if (times.length < 3) return 33;
  const d: number[] = [];
  for (let i = 1; i < times.length; i++) d.push(times[i] - times[i - 1]);
  return median(d);
}

function countValid(track: readonly (FootObs | null)[], times: readonly number[], a: number, b: number): number {
  let n = 0;
  for (let i = 0; i < track.length; i++) if (track[i] && times[i] >= a && times[i] < b) n++;
  return n;
}

function within(track: readonly (FootObs | null)[], times: readonly number[], a: number, b: number): FootObs[] {
  const out: FootObs[] = [];
  for (let i = 0; i < track.length; i++) if (track[i] && times[i] >= a && times[i] <= b) out.push(track[i]!);
  return out;
}
